import { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, MessageCircleQuestion, Plus, Save } from "lucide-react";

// PROMPT 132 (2026-10-16): agregar o editar UNA pregunta típica (la pantalla "Preguntas típicas").
// Es la misma entrada de "Qué sabe" (tipo respuesta): lo que se guarda acá lo ve el agente igual.
// Vale para todas las marcas o para una sola. Si la entrada es de un tour, el tour no se cambia desde acá.

const MAX_PREGUNTA = 400;
const MAX_RESPUESTA = 2000;
const MAX_TEMA = 40;

const CAMPO = "h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700";

// La pregunta sin mayúsculas, acentos ni signos, para saber si dos son la misma.
export const clavePregunta = (texto) =>
  String(texto || "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export default function PreguntaTipicaDialog({
  open,
  onOpenChange,
  item = null,
  marcas = [],
  tours = [],
  temas = [],
  items = [],
  onGuardado,
}) {
  const esTour = item?.alcance === "tour";
  const [form, setForm] = useState(() => ({
    pregunta: item?.pregunta || "",
    respuesta: item?.respuesta || "",
    tema: item?.tema || "",
    marca: item?.alcance === "marca" ? item.marca_slug || "" : "",
  }));
  const [guardando, setGuardando] = useState(false);

  const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const nombreMarca = (m) => m.remitente_nombre || m.nombre || m.slug;
  const nombreTour = tours.find((t) => t.id === item?.tour_id)?.nombre || item?.tour_id || "";

  // ¿Ya hay una igual para la misma marca? (solo entre las prendidas)
  const parecida = useMemo(() => {
    const clave = clavePregunta(form.pregunta);
    if (!clave || esTour) return null;
    return (
      items.find(
        (it) =>
          it.id !== item?.id &&
          it.activo !== false &&
          (it.alcance || "siempre") === (form.marca ? "marca" : "siempre") &&
          (!form.marca || it.marca_slug === form.marca) &&
          clavePregunta(it.pregunta) === clave
      ) || null
    );
  }, [items, item, esTour, form.pregunta, form.marca]);

  const nueva = !item;
  const puedeGuardar =
    form.pregunta.trim() && form.respuesta.trim() && form.tema.trim().length <= MAX_TEMA && !(nueva && parecida);

  const guardar = async () => {
    const cuerpo = {
      tipo: "respuesta",
      pregunta: form.pregunta.trim(),
      respuesta: form.respuesta.trim(),
      tema: form.tema.trim(),
      alcance: esTour ? "tour" : form.marca ? "marca" : "siempre",
      marca_slug: !esTour && form.marca ? form.marca : null,
      tour_id: esTour ? item.tour_id : null,
    };
    setGuardando(true);
    try {
      if (nueva) await api.post("/whatsapp/conocimiento", cuerpo);
      else await api.put(`/whatsapp/conocimiento/${item.id}`, cuerpo);
      toast.success(nueva ? "Listo, el agente ya la sabe." : "Cambio guardado.");
      onGuardado?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pregunta-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MessageCircleQuestion className="h-5 w-5" /> {nueva ? "Agregar una pregunta típica" : "Editar la pregunta"}
          </DialogTitle>
          <DialogDescription>
            Lo que el cliente suele preguntar y cómo hay que contestarle. El agente lo usa en la próxima conversación.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="pregunta-form-pregunta" className="text-xs">Lo que pregunta el cliente</Label>
            <Input
              id="pregunta-form-pregunta"
              value={form.pregunta}
              onChange={(e) => set("pregunta", e.target.value)}
              maxLength={MAX_PREGUNTA}
              placeholder="¿El bus tiene baño?"
              data-testid="pregunta-form-pregunta"
            />
            <p className="text-[11px] text-slate-400">
              No hace falta que lo escriba igual: si pregunta parecido, el agente igual la encuentra.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pregunta-form-respuesta" className="text-xs">Qué tiene que contestar</Label>
            <textarea
              id="pregunta-form-respuesta"
              value={form.respuesta}
              onChange={(e) => set("respuesta", e.target.value)}
              maxLength={MAX_RESPUESTA}
              rows={5}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              placeholder="Los buses a Washington, Boston y Niágara tienen baño. Los tours dentro de la ciudad no, pero paramos."
              data-testid="pregunta-form-respuesta"
            />
            <p className="text-[11px] text-slate-400 text-right">{form.respuesta.length} de {MAX_RESPUESTA} caracteres</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="pregunta-form-tema" className="text-xs">Tema (para ordenarlas)</Label>
              <Input
                id="pregunta-form-tema"
                list="pregunta-form-temas"
                value={form.tema}
                onChange={(e) => set("tema", e.target.value)}
                maxLength={MAX_TEMA}
                placeholder="Equipaje, Pagos, Horarios…"
                data-testid="pregunta-form-tema"
              />
              <datalist id="pregunta-form-temas">
                {temas.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pregunta-form-marca" className="text-xs">Para qué marca</Label>
              <select
                id="pregunta-form-marca"
                value={form.marca}
                onChange={(e) => set("marca", e.target.value)}
                disabled={esTour}
                className={CAMPO}
                data-testid="pregunta-form-marca"
              >
                <option value="">Todas las marcas</option>
                {marcas.map((m) => (
                  <option key={m.slug} value={m.slug}>{nombreMarca(m)}</option>
                ))}
                {form.marca && !marcas.some((m) => m.slug === form.marca) ? (
                  <option value={form.marca}>{form.marca}</option>
                ) : null}
              </select>
            </div>
          </div>
          {esTour && (
            <p className="text-xs text-violet-700" data-testid="pregunta-form-aviso-tour">
              Esta respuesta vale solo para el tour «{nombreTour}». Para cambiar a qué tour o marca vale, usá «Qué sabe»
              en el Agente WhatsApp.
            </p>
          )}
          {parecida && (
            <p className="text-xs text-amber-700" data-testid="pregunta-form-parecida">
              {nueva ? "Ya está cargada" : "Hay otra igual"}
              {form.marca ? " para esta marca" : " para todas las marcas"}: «{parecida.pregunta}».{" "}
              {nueva ? "Editala desde la lista en vez de agregar otra." : "Conviene dejar una sola."}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="pregunta-form-cancelar">
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={guardando || !puedeGuardar} data-testid="pregunta-form-guardar" className="gap-1.5">
            {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : nueva ? <Plus className="h-4 w-4" /> : <Save className="h-4 w-4" />}
            {nueva ? "Enseñárselo" : "Guardar el cambio"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
